import { DateTimeEditor } from '@/components/UI/SpreadSheet/editors/DateTimeEditor';
import type { CustomColumnSettings } from '@/components/UI/SpreadSheet/SpreadSheet.model';
import { capitalise, renderMetadataValue } from '@/helpers';
import { _isNil, _isNotEmpty, _notNil, safelyParseJson } from '@/littledash';
import type { Animal } from '@/model/Animal.model';
import type { MetadataField } from '@/model/Metadata.model';
import type { ID, ISODateTime } from '@/model/Common.model';
import type { BulkSamplesFormData, SampleDetails } from '@/model/Sample.model';
import { defaultMetadataToColumnSettingMapper, formatColumnSettingsDataValue } from '@/support/hot';
import { TimepointEditor } from '@/components/UI/SpreadSheet/editors/TimepointEditor';

export const toFiniteNumberOrNull = (value: unknown): number | null => {
  if (_isNil(value)) {
    return null;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

export interface SampleData {
  [key: string]: unknown;
  cageName?: string;
  subject_id: ID;
  subjectName: string;
  sampleType: string;
  date: ISODateTime;
  details: Record<string, string>;
  timepoint?: string;
  comments: string;
  meta: Record<string, string>;
}

const getSampleMeta = (meta: Record<string, unknown>, metadata: Array<MetadataField>) =>
  Object.entries(meta).reduce<Record<string, string>>((acc, [metaSlug, value]) => {
    const glossaryMeta = metadata.find(({ slug }) => slug === metaSlug);
    if (_notNil(glossaryMeta)) {
      const encodedKey = formatColumnSettingsDataValue(glossaryMeta.title);
      if (glossaryMeta.field_type === 'date') {
        acc[encodedKey] = value as string;
      } else if (Array.isArray(value)) {
        acc[encodedKey] = value.toString();
      } else if (Array.isArray(safelyParseJson(value as string))) {
        acc[encodedKey] = JSON.parse(value as string).join(',');
      } else {
        acc[encodedKey] = renderMetadataValue({
          value: value as string,
          field_type: glossaryMeta.field_type,
        });
      }
    }
    return acc;
  }, {});

const getSampleDetails = (details: SampleDetails) =>
  Object.entries(details).reduce<Record<string, string>>((acc, [detail, value]) => {
    acc[detail] = value.value;
    return acc;
  }, {});

export const generateSampleData = (
  subjects: Array<Animal>,
  metadata: Array<MetadataField>,
  sampleDetails: BulkSamplesFormData
): Array<SampleData> =>
  subjects.reduce<Array<SampleData>>((acc, { id: subject_id, cage, name }) => {
    const { sample_type, date, comments, details, timepoint, meta = {} } = sampleDetails;

    const sampleMeta = _isNotEmpty(meta) ? getSampleMeta(meta, metadata) : {};

    acc.push({
      cageName: cage?.name,
      subject_id,
      subjectName: name ?? '',
      sampleType: sample_type.title ?? '',
      date,
      comments,
      meta: sampleMeta,
      timepoint,
      details: getSampleDetails(details ?? {}),
    });

    return acc;
  }, []);

export const generateColumns = (
  sampleDetails: BulkSamplesFormData,
  metadata: Array<MetadataField>,
  plannedSamplesEnabled = false
) => {
  const columns: Array<CustomColumnSettings> = [
    {
      data: 'cageName',
      readOnly: true,
      type: 'text',
      width: 150,
    },
    {
      data: 'subjectName',
      readOnly: true,
      type: 'text',
      width: 150,
    },
    {
      data: 'sampleType',
      readOnly: true,
      type: 'text',
      width: 150,
    },
    {
      data: 'date',
      readOnly: false,
      type: DateTimeEditor.type,
      width: 200,
    },
  ];

  const colHeaders = ['Cage', 'Animal name', 'Sample type', 'Date taken'];

  Object.entries(sampleDetails.details ?? {}).forEach(([detail, value]) => {
    columns.push({
      data: `details.${detail}`,
      readOnly: false,
      type: 'numeric',
      width: 150,
    });
    colHeaders.push(`${capitalise(detail)} (${value.unit})`);
  });

  if (plannedSamplesEnabled) {
    colHeaders.push('Timepoint');
    columns.push({
      data: 'timepoint',
      readOnly: false,
      type: TimepointEditor.type,
      singleValue: true,
      allow_zero: true,
      allow_negative: true,
    } as CustomColumnSettings);
  }

  colHeaders.push('Comments');
  columns.push({
    data: 'comments',
    readOnly: false,
    type: 'text',
    width: 150,
    validator: function (value: string, callback: (valid: boolean) => void) {
      callback((value ?? '').length <= 255);
    },
    validationErrorMessage: () => 'Comments have a max length of 255 characters',
  } as CustomColumnSettings);

  if (_isNotEmpty(metadata)) {
    metadata.forEach((meta) => {
      const { columnHeader, columnSettings } = defaultMetadataToColumnSettingMapper(meta);
      colHeaders.push(columnHeader);
      columns.push(columnSettings as CustomColumnSettings);
    });
  }

  return { colHeaders, columns };
};
